import React, { useEffect, useState, useMemo } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { TrendingUp, AlertCircle, Wallet, RefreshCcw, StickyNote } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';
import InfoTip from './InfoTip';
import PrintButton from './PrintButton';

import { makeFmt } from './currency';

const CURVES = [
  { v: 's_curve', label: 'S-Curve (typical)' },
  { v: 'linear', label: 'Linear' },
  { v: 'front_loaded', label: 'Front-loaded' },
  { v: 'back_loaded', label: 'Back-loaded' },
];

export default function FundingDrawdown({ calculation, riskAdjustedCost, revisedBurn }) {
  const fmt$ = useMemo(() => makeFmt(calculation), [calculation]);
  const [curve, setCurve] = useState('s_curve');
  const [initial, setInitial] = useState(0);
  const [funding, setFunding] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState({});  // { monthIndex: 'note text' }
  const [showNoteFor, setShowNoteFor] = useState(null);
  const [usedRevisedBurn, setUsedRevisedBurn] = useState(false);

  const totalCost = (riskAdjustedCost || calculation?.final_cost_usd) || 0;
  const months = Math.max(1, Math.round(calculation?.project_duration_months || 12));

  useEffect(() => {
    // Initialize funding to even split
    if (months > 0) {
      setFunding(Array.from({ length: months }, () => Math.round(totalCost / months)));
    }
  }, [months, totalCost]);

  // When BaselineActuals pushes a revised burn, drop it straight into funding
  useEffect(() => {
    if (!revisedBurn?.monthly_plan_usd?.length) return;
    setFunding(revisedBurn.monthly_plan_usd.map(v => Math.round(v)));
    setUsedRevisedBurn(true);
    if (revisedBurn.curve) setCurve(revisedBurn.curve);
    toast.message('Funding plan updated', {
      description: `Revised burn applied — remaining ${revisedBurn.months_remaining}mo · ${'$' + Number(revisedBurn.revised_monthly_burn_usd).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo`,
    });
  }, [revisedBurn]);

  const run = async () => {
    if (!calculation) return;
    try {
      setLoading(true);
      const res = await ConstructionAPI.analyzeFunding({
        total_cost_usd: totalCost,
        duration_months: months,
        burn_curve: curve,
        funding_per_month_usd: funding,
        initial_funding_usd: initial,
      });
      setAnalysis(res);
    } catch (err) {
      toast.error('Funding analysis failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (calculation && funding.length === months) run(); /* eslint-disable-next-line */ }, [curve, initial, funding, calculation?.id, totalCost]);

  const updateFundingMonth = (i, val) => {
    setFunding(prev => prev.map((f, idx) => idx === i ? Number(val) : f));
  };
  const distributeEvenly = () => setFunding(Array.from({ length: months }, () => Math.round(totalCost / months)));
  const distributeMatchBurn = () => {
    if (!analysis) return;
    setFunding(analysis.monthly_burn_usd.map(v => Math.round(v)));
  };

  if (!calculation) {
    return <Card><CardContent className="p-8 text-center text-sm text-slate-500">Run a calculation first to analyse funding drawdown.</CardContent></Card>;
  }

  const chartData = analysis ? analysis.monthly_burn_usd.map((b, i) => ({
    month: `M${i + 1}`,
    Burn: b,
    Funding: analysis.monthly_funding_usd[i],
    'Cum Burn': analysis.cumulative_burn_usd[i],
    'Cum Funding': analysis.cumulative_funding_usd[i],
    Gap: analysis.monthly_gap_usd[i],
  })) : [];

  return (
    <div className="space-y-4" data-testid="funding-tab">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Pill label="Total cost" value={fmt$(analysis?.total_cost_usd)} accent="bg-orange-600" icon={<TrendingUp className="h-4 w-4" />} />
        <Pill label="Total funding" value={fmt$(analysis?.total_funding_usd)} accent="bg-emerald-600" icon={<Wallet className="h-4 w-4" />} />
        <Pill label="Final gap" value={fmt$(analysis?.final_gap_usd)} accent={analysis?.final_gap_usd >= 0 ? 'bg-emerald-700' : 'bg-red-600'} icon={<TrendingUp className="h-4 w-4" />} />
        <Pill label="Peak burn month" value={analysis ? `M${analysis.peak_burn_month}` : '—'} accent="bg-indigo-600" icon={<TrendingUp className="h-4 w-4" />} />
        <Pill label="Shortfall months" value={`${analysis?.shortfall_months?.length || 0}`} accent={(analysis?.shortfall_months?.length || 0) > 0 ? 'bg-red-600' : 'bg-emerald-600'} icon={<AlertCircle className="h-4 w-4" />} />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Drawdown configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="text-xs text-slate-600">Burn curve</label>
              <Select value={curve} onValueChange={setCurve}>
                <SelectTrigger data-testid="burn-curve-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURVES.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Initial funding (USD)</label>
              <Input type="number" value={initial} onChange={e => setInitial(Number(e.target.value))} data-testid="initial-funding-input" />
            </div>
            <div className="md:col-span-2 flex items-end gap-2">
              <Button size="sm" variant="outline" onClick={distributeEvenly}>Distribute funding evenly</Button>
              <Button size="sm" variant="outline" onClick={distributeMatchBurn} disabled={!analysis}>Match burn</Button>
              <Button size="sm" variant="outline" onClick={run} disabled={loading}><RefreshCcw className="h-3.5 w-3.5 mr-1" />Recompute</Button>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => fmt$(v, { compact: true })} />
              <Tooltip formatter={(v) => fmt$(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Burn" fill="#f97316" />
              <Bar dataKey="Funding" fill="#10b981" />
              <Line type="monotone" dataKey="Cum Burn" stroke="#f59e0b" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Cum Funding" stroke="#059669" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>

          <div className="mt-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Cumulative gap (funding − burn)</h4>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} tickFormatter={(v) => fmt$(v, { compact: true })} />
                <Tooltip formatter={(v) => fmt$(v)} />
                <Area type="monotone" dataKey="Gap" stroke="#0ea5e9" fill="#bae6fd" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {(analysis?.shortfall_months?.length || 0) > 0 && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
              <div className="font-semibold text-red-800 mb-1.5 inline-flex items-center gap-1.5"><AlertCircle className="h-4 w-4" />Cash-flow alert</div>
              <p className="text-red-700 text-xs">
                Funding falls short of burn in <strong>{analysis.shortfall_months.length}</strong> month(s). Maximum gap: <strong>{fmt$(Math.abs(analysis.max_shortfall_usd))}</strong>.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {analysis.shortfall_months.map(s => (
                  <Badge key={s.month} className="bg-red-100 text-red-800 border border-red-300 text-xs">M{s.month} · −{fmt$(s.shortfall_usd)}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base inline-flex items-center gap-1.5">
                Monthly funding plan
                {usedRevisedBurn && (
                  <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200" data-testid="funding-revised-badge">
                    REVISED FROM VARIANCE
                  </span>
                )}
                <InfoTip>Each cell is the cash inflow planned for that month. Click the note icon under a cell to add context (e.g. "tranche 2 release", "delayed by sponsor").</InfoTip>
              </CardTitle>
              <p className="text-xs text-slate-500">Edit each month&apos;s funding inflow and add notes. The chart updates live.</p>
            </div>
            <PrintButton testid="funding-print-btn" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-12 gap-2">
            {funding.map((f, i) => (
              <div key={i} className="text-center">
                <div className="text-[10px] text-slate-500 mb-1">M{i + 1}</div>
                <Input type="number" value={f} onChange={e => updateFundingMonth(i, e.target.value)} className="h-8 text-xs text-right" data-testid={`funding-m${i + 1}`} />
                <button
                  onClick={() => setShowNoteFor(showNoteFor === i ? null : i)}
                  className={`mt-1 text-[10px] inline-flex items-center gap-0.5 ${notes[i] ? 'text-orange-600 font-semibold' : 'text-slate-400'} hover:text-slate-700`}
                  data-testid={`funding-note-toggle-${i + 1}`}
                  title={notes[i] || 'Add note'}
                >
                  <StickyNote className="h-3 w-3" />{notes[i] ? 'note' : 'add'}
                </button>
                {showNoteFor === i && (
                  <Input
                    autoFocus
                    placeholder="Note for this month…"
                    value={notes[i] || ''}
                    onChange={e => setNotes(n => ({ ...n, [i]: e.target.value }))}
                    onBlur={() => setShowNoteFor(null)}
                    onKeyDown={e => e.key === 'Enter' && setShowNoteFor(null)}
                    className="h-7 text-[10px] mt-1"
                    data-testid={`funding-note-input-${i + 1}`}
                  />
                )}
              </div>
            ))}
          </div>
          {Object.keys(notes).filter(k => notes[k]).length > 0 && (
            <div className="mt-3 text-xs text-slate-600 border-t pt-2" data-testid="funding-notes-summary">
              <strong>Notes:</strong>
              <ul className="mt-1 space-y-0.5">
                {Object.entries(notes).filter(([, v]) => v).map(([k, v]) => (
                  <li key={k}><span className="text-orange-600 font-semibold">M{Number(k) + 1}:</span> {v}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Pill({ label, value, accent, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
      <div className={`p-2 rounded-lg text-white ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500 truncate">{label}</div>
        <div className="font-semibold text-slate-900 truncate text-sm">{value}</div>
      </div>
    </div>
  );
}
